import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import Tabs from 'components/application/Tabs';

import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import PersonnelDetailsTab from 'components/personnel/PersonnelDetails';
import PersonnelSidePanelLineManagerTab from 'components/personnel/PersonnelSidePanelLineManagerTab';
import PersonnelSidePanelSubordinatesTab from 'components/personnel/PersonnelSidePanelSubordinatesTab';
import PersonnelSidePanelTeamsTab from 'components/personnel/PersonnelSidePanelTeamsTab';

const defaultSubordinate = {
  loaded: false,
  collection: [],
  metaData: {
    totalCount: null,
    isLastPage: false,
    currentPage: 0,
    totalPages: 0,
  },
};
export default function PersonnelSidePanelTabs({
  currentPersonnel,
  defaultDetails,
  isCompact,
  lineManager,
  sidePanelIsOpen,
  teams,
}) {
  const breadBoard = useBreadBoard();

  const [loadMoreRef, loadMoreInView] = useInView();

  const [currentSubordinates, setSubordinates] = useState(defaultSubordinate);
  const [activeTab, setActiveTab] = useState('details');
  const prevSidePanelIsOpen = useRef(false);

  const { hasTeamViewableAccess } = useTrainingRegisterResources();

  const { t: i18nTCommon } = useTranslation('common');

  const fetchSubordinates = () => {
    axios
      .get(`/personnel/${currentPersonnel.id}/subordinates`, {
        params: {
          page: parseInt(currentSubordinates.metaData.currentPage, 10) + 1,
        },
      })
      .then((response) => {
        setSubordinates((prevState) => ({
          loaded: true,
          collection: prevState.collection.concat(response.data.data),
          metaData: response.data.meta,
        }));
      })
      .catch(breadBoard.addInedibleToast);
  };

  (function resetOnPanelTransition() {
    if (!prevSidePanelIsOpen.current && sidePanelIsOpen)
      setActiveTab('details');
    if (prevSidePanelIsOpen.current && !sidePanelIsOpen)
      setSubordinates(defaultSubordinate);
    prevSidePanelIsOpen.current = sidePanelIsOpen;
  })();

  useEffect(() => {
    if (sidePanelIsOpen) fetchSubordinates();
  }, [sidePanelIsOpen]);

  useEffect(() => {
    if (!currentSubordinates.metaData.isLastPage && loadMoreInView)
      fetchSubordinates({ increment: true });
  }, [currentSubordinates.metaData.isLastPage, loadMoreInView]);

  return (
    <div>
      <Tabs
        activeTab={activeTab}
        modifiers={['panelled']}
        navClassName='tw-bg-grey-050'
        onTabClick={setActiveTab}
      >
        <PersonnelDetailsTab
          currentPersonnel={currentPersonnel}
          defaultDetails={defaultDetails}
          id='details'
          isAllVisible={true}
          isCompact={isCompact}
          key='detailsTab'
          label={i18nTCommon('shared.details')}
        />
        {hasTeamViewableAccess && (
          <PersonnelSidePanelTeamsTab
            id='teams'
            key='teamsTab'
            label={i18nTCommon('shared.teams')}
            teams={teams}
          />
        )}
        <PersonnelSidePanelLineManagerTab
          id='lineManager'
          key='lineManagerTab'
          label={i18nTCommon('shared.lineManager')}
          lineManager={lineManager}
        />
        <PersonnelSidePanelSubordinatesTab
          disabled={!currentSubordinates.loaded}
          id='directReports'
          key='subordinatesTab'
          label={i18nTCommon('personnel.directReports')}
          loadMoreRef={loadMoreRef}
          metaData={currentSubordinates.metaData}
          subordinates={currentSubordinates.collection}
        />
      </Tabs>
    </div>
  );
}

PersonnelSidePanelTabs.propTypes = {
  defaultDetails: PropTypes.array,
  currentPersonnel: PropTypes.object,
  teams: PropTypes.array,
  sidePanelIsOpen: PropTypes.bool,
  isCompact: PropTypes.bool,
  lineManager: PropTypes.object,
};
